import React, { useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import cn from 'classnames';
import Cookies from 'js-cookie';
import { AutocompleteComponents } from '@algolia/autocomplete-js';

import { HitVariants, PriceGroups } from '../Hit';
import { useHitPrice } from '@hooks/useHitPrice';
import Tag from '@components/ui/Tag';
import { ProductTag } from '@adapters/bigcommerce/types/product';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import { FEATURE_FLAG_MEMBER_PRICING } from '@config/feature-flag';

import styles from './ProductItem.module.scss';
import { tagsFromFile } from '@adapters/bigcommerce/transforms/utils';

type ProductHit = {
  // looks similar to components/algolia/Hit/index.tsx HitProps, may be able to combine eventually
  brand: string;
  categories: string[];
  description: string;
  image: string;
  title: string;
  price: number;
  prices?: PriceGroups;
  salePrice: number;
  rating: number;
  url: string;
  __autocomplete_queryID?: string;
  colour: string;
  variants: HitVariants;
  tags: ProductTag[];
  sku: string;
};

type ProductItemProps = {
  hit: ProductHit;
  locale?: string;
  components: AutocompleteComponents;
};

export function ProductItem({ hit, locale }: ProductItemProps) {
  const { colour, variants } = hit;

  const {
    priceFormatted,
    salePriceFormatted,
    normalPriceRange,
    salePriceRange,
    guestPriceFormatted,
    guestNormalPriceRange,
    shouldShowGuestPriceComparison,
  } = useHitPrice(colour, variants, locale);

  const hitHasSalePrice = salePriceFormatted || salePriceRange;
  const hitHasNormalPrice = priceFormatted || normalPriceRange;
  const hitHasGuestPrice = guestPriceFormatted || guestNormalPriceRange;
  const showGuestPriceComparison =
    !hitHasSalePrice &&
    shouldShowGuestPriceComparison &&
    hitHasGuestPrice &&
    FEATURE_FLAG_MEMBER_PRICING;

  const showTags = useCallback(
    (tags: ProductTag[]) => {
      if (!tags || tags?.length === 0) {
        return false;
      }

      const allTags = tagsFromFile(locale ?? 'en');
      const tagInfo = tags[0]?.id ? allTags[tags[0].id] : null;

      const variantTagMatchesHitSku = tagInfo?.variants?.includes(hit.sku);
      const checkForVariants = tagInfo?.variants.length !== 0;

      return variantTagMatchesHitSku || !checkForVariants;
    },
    [hit.sku, locale]
  );
  const showTag = showTags(hit.tags);

  const handleItemClick = () => {
    Cookies.set(
      ALGOLIA_CHECKOUT_QUERY_ID_NAME,
      hit.__autocomplete_queryID || ''
    );
  };

  return (
    <Link href={hit.url} className={styles.item} onClick={handleItemClick}>
      {hit.image ? (
        <div className={styles.itemImageContainer}>
          <Image
            src={hit.image}
            alt={hit.title}
            className={styles.itemImage}
            fill
            sizes="(max-width: 768px) 100vw, 60px"
          />
        </div>
      ) : null}

      <div className={styles.itemContent}>
        <div>{hit.title}</div>

        <div className={styles.itemPrices}>
          {hitHasSalePrice ? (
            <span className={styles.hitSalePrice}>
              {salePriceRange ? salePriceRange : salePriceFormatted}
            </span>
          ) : null}

          {hitHasNormalPrice ? (
            <span
              className={cn(styles.hitPrice, {
                [styles.hitOldPrice]: hitHasSalePrice,
              })}
            >
              {normalPriceRange ? normalPriceRange : priceFormatted}
            </span>
          ) : null}

          {showGuestPriceComparison && hitHasGuestPrice ? (
            <span className={cn(styles.hitPrice, styles.hitOldPrice)}>
              {guestNormalPriceRange
                ? guestNormalPriceRange
                : guestPriceFormatted}
            </span>
          ) : null}
          {showTag && (
            <span className={styles.tags}>
              <Tag
                tagText={hit.tags?.[0]?.name}
                colourOverride={hit.tags?.[0]?.colourOverride}
              />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
